import axios from 'axios';
import { useMemo } from 'react';

// integrate with app AuthProvider context
import { useAuth } from '@/shared/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Tenant basePath integration
//
// axiosInstance is created as a module-level singleton before React renders,
// so it cannot read React context directly. EcosystemProvider resolves the
// active ecosystem on boot and pushes the resulting basePath here via
// setEcosystemBasePath(). A request interceptor then reads this module
// variable and prepends it to relative /api requests.
//
// - Subdomain / platform mode: basePath is '' → interceptor is a no-op,
//   request URLs are byte-identical to pre-multi-tenant behavior.
// - Path mode: basePath is '/<slug>' → '/api/organizations' becomes
//   '/<slug>/api/organizations' so the server tenant middleware can resolve
//   the ecosystem from the path prefix.
// ---------------------------------------------------------------------------
let ecosystemBasePath = '';

/**
 * Set the tenant basePath used to prefix relative /api request URLs.
 * Called by EcosystemProvider once the active ecosystem is resolved.
 *
 * @param {string} basePath - '' for subdomain/platform mode, '/<slug>' for path mode.
 */
export const setEcosystemBasePath = (basePath) => {
    ecosystemBasePath = basePath || '';
};

/**
 * Apply the resolved tenant basePath to a request config's URL.
 *
 * Only relative paths beginning with '/api' are rewritten. Absolute URLs
 * (http://, https://) and non-API paths are left untouched, and the
 * prefix is never applied twice.
 *
 * @param {Object} config - Axios request config.
 * @returns {Object} The (possibly) rewritten config.
 */
const applyEcosystemBasePath = (config) => {
    if (!ecosystemBasePath) return config;

    const url = config.url;
    if (typeof url !== 'string' || url.length === 0) return config;

    // Leave absolute URLs alone.
    if (/^https?:\/\//i.test(url)) return config;

    // Only rewrite relative API paths.
    if (!url.startsWith('/api')) return config;

    // Guard against double-prepending.
    if (url.startsWith(`${ecosystemBasePath}/`)) return config;

    config.url = `${ecosystemBasePath}${url}`;
    return config;
};

export const createAuthenticatedAxios = (user) => {
    // create axios instance
    const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000', 10),
        withCredentials: true, // matches CORS with credentials
    });

    // request axios interceptor
    axiosInstance.interceptors.request.use(
        async (config) => {
            // Prepend the tenant basePath (no-op when basePath is empty).
            applyEcosystemBasePath(config);

            try {
                if (user) {
                    const token = await user.getIdToken();
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } catch (error) {
                console.error('Error retrieving Firebase token: ', error);
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // response interceptor for token refresh and error handling
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // handle 401 errors with automatic token refresh
            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                try {
                    if (user) {
                        const newToken = await user.getIdToken(true); //force refresh
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axiosInstance(originalRequest);
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed: ', refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    return axiosInstance;
};

// hook for returning authenticated axios instance
export const useAuthenticatedAxios = () => {
    const { user } = useAuth();
    return useMemo(() => {
        return createAuthenticatedAxios(user);
    }, [user]);
};

export default createAuthenticatedAxios;
